import { zeroDate } from 'constant/common';
import dayjs from 'dayjs';

/**
 * Converts a duration string to a human-readable format.
 * @param {string} duration - The duration string in the format "HH:MM:SS".
 * @returns {string} A human-readable representation of the duration (e.g., "1h 30m").
 */
export const convertDuration = (duration: string) => {
  const [hours, minutes] = duration.split(':').map(Number);
  let result = '';

  if (hours > 0) {
    result += `${hours}h`;
  }

  if (minutes > 0 && hours > 0) {
    result += ` ${minutes}m`;
  } else if (minutes > 0) {
    result += `${minutes}m`;
  }

  return result.trim();
};

/**
 * Converts a time string in 24-hour format to 12-hour format with AM/PM.
 * @param {string} time - The time string in 24-hour format (e.g., '15:00:00').
 * @returns {string} The time string formatted in 12-hour format with AM/PM (e.g., '3:00 PM').
 */
export const convertTimeToAMPMFormat = (time: string): string => {
  const parsedTime = dayjs(time, 'HH:mm:ss');
  return parsedTime.format('h:mm A');
};

/**
 * Convert time string to minutes.
 * @param {string} time - The time string in the format "HH:MM:SS".
 * @returns {number} Total minutes.
 */
export const timeToMinutes = (time: string): number => {
  const [hours, minutes, seconds] = time.split(':').map(Number);
  return hours * 60 + minutes + seconds / 60;
};

/**
 * Calculate the duration between two time strings.
 * @param {string} startTime - The start time string in the format "HH:MM:SS".
 * @param {string} endTime - The end time string in the format "HH:MM:SS".
 * @returns {string} Duration in the format "HH:MM:SS".
 */

export const calculateDuration = (
  startTime: string,
  endTime: string
): string => {
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);
  const totalMinutes = endMinutes - startMinutes;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.floor(totalMinutes % 60);
  const seconds = Math.floor((totalMinutes % 1) * 60);

  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Formats a duration string to a more readable format.
 * Converts an ISO duration string into a format like "1h 30m" or "15m".
 *
 * @param {string} durationStr - The ISO duration string to format.
 * @returns {string} - The formatted duration string.
 *
 * @example
 * const totalDuration = "1899-12-30T01:30:00.000Z";
 * console.log(formatDuration(totalDuration)); // Outputs: "1h 30m"
 */

export const formatDuration = (durationStr: string): string => {
  const baseDate = dayjs(zeroDate);
  const durationDate = dayjs(durationStr);
  const diffInMinutes = durationDate.diff(baseDate, 'minute');

  const hours = Math.floor(diffInMinutes / 60);
  const minutes = diffInMinutes % 60;

  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};
